<script setup>
import { computed, onMounted, reactive, ref, watch, getCurrentInstance, nextTick } from 'vue'
import { router } from '@inertiajs/vue3';
import { watchDebounced, useStorage } from '@vueuse/core';
import MainLayout from '@/layout/main/MainLayout.vue';
import { useEnhancedLoading } from '@/composables/useEnhancedLoading';
import { useCart } from '@/composables/useCart';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import RadioButton from 'primevue/radiobutton';
import Toast from 'primevue/toast';
import { useToast } from 'primevue/usetoast';
import Select from 'primevue/select';
import FileUpload from 'primevue/fileupload';
import Textarea from 'primevue/textarea';
import RocketFillSvg from '@/assets/rocket-icon-fill.svg?raw';
import draggable from 'vuedraggable';
import Checkbox from 'primevue/checkbox';
import SitesFilters from '@/views/linkarme/components/SitesFilters.vue';
import SitesTable from '@/views/linkarme/components/SitesTable.vue';
import SyncBanner from '@/components/SyncBanner.vue';


defineOptions({
    layout: MainLayout,
});

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const { showPageLoading, hidePageLoading } = useEnhancedLoading('sites-table');
const toast = useToast();
const { loadCart } = useCart();

const rocketFillSvg = computed(() => {
    return RocketFillSvg || '';
});


// Mock coinStore para evitar erro do Pinia
const coinStore = reactive({
    coin: 'USD',
    ratios: {
        'BRL': 1,
        'EUR': 1,
        'USD': 1,
        'GBP': 1
    }
})

const props = defineProps({
    title: String,
    sites: Object,
    coins: Object,
    countries: Array,
    languages: Array,
    categories: Array,
    sellers: Array,
    types: Array,
    favorites: Array,
    interests: Array,
    projects: Array,
    sitesByCountry: Array,
    importFailures: Object,
    importErrors: Object,
    importAdded: Array,
    importUpdated: Array,
    pendingCount: Number,
    filters: Object,
});

const links = computed(() => {
    const _links = props.sites.links
    _links.shift()
    _links.pop()
    return _links
})

const sitesWithHighestSale = computed(() => {
    const _sites = JSON.parse(JSON.stringify(props.sites.data))

    _sites.forEach(site => {
        // Backend provides highest_sale in cents — keep in cents for formatConvertedPrice/formatCents
        site.highest_sale = site.highest_sale || 0
        site.highest_sale_coin_ratio = site.highest_sale_coin ? coinStore.ratios[site.highest_sale_coin] : 1
    })

    return _sites
})

// Define valid column keys to filter out old/invalid columns
const validColumnKeys = [
    'sale', 'url', 'da', 'dr', 'traffic', 'link_type', 'branded_content', 'types',
    'country', 'language', 'category', 'example',
];

// Column label map - keys only, labels added reactively
const columnDefinitions = {
    sale: { key: 'sale', sortable: true, defaultVisible: true },
    url: { key: 'url', sortable: true, defaultVisible: true },
    da: { key: 'da', sortable: true, defaultVisible: true },
    dr: { key: 'dr', sortable: true, defaultVisible: true },
    traffic: { key: 'traffic', sortable: true, defaultVisible: true },
    link_type: { key: 'link_type', sortable: false, defaultVisible: true },
    branded_content: { key: 'branded_content', sortable: false, defaultVisible: true },
    types: { key: 'types', sortable: false, defaultVisible: true },
    country: { key: 'country', sortable: false, defaultVisible: false },
    language: { key: 'language', sortable: false, defaultVisible: false },
    category: { key: 'category', sortable: false, defaultVisible: false },
    example: { key: 'example', sortable: false, defaultVisible: false },
};

// Get label for a column key
const getColumnLabel = (key) => {
    const labelMap = {
        sale: 'Price',
        url: 'Domain',
        da: 'DA',
        dr: 'DR',
        traffic: 'Traffic',
        link_type: 'Link Type',
        branded_content: 'Branded Content',
        types: 'Accepts niche',
        country: 'Country',
        language: 'Language',
        category: 'Category',
        example: 'Example URL',
    };
    return $t(labelMap[key] || key);
};

// Initialize columns from storage or defaults
const storedColumnsRaw = useStorage('sites.client.index.columns', null);

// Build initial columns
const initialColumns = storedColumnsRaw.value && Array.isArray(storedColumnsRaw.value)
    ? storedColumnsRaw.value.filter(col => validColumnKeys.includes(col.key))
    : null;

// If no valid stored columns or invalid columns found, use defaults
if (!initialColumns || initialColumns.length === 0) {
    const defaultCols = validColumnKeys.map(key => ({
        key,
        visible: columnDefinitions[key].defaultVisible,
        sortable: columnDefinitions[key].sortable,
    }));
    storedColumnsRaw.value = defaultCols;
}

// Internal ref for column order and visibility (without labels)
const columnsData = ref(storedColumnsRaw.value || []);

// Watch for changes to columnsData and save to storage
watch(columnsData, (newColumns) => {
    storedColumnsRaw.value = newColumns.map(col => ({
        key: col.key,
        visible: col.visible,
        sortable: col.sortable
    }));
}, { deep: true });

// Computed columns with labels always applied
const columns = computed({
    get() {
        return columnsData.value.map(col => ({
            ...col,
            label: getColumnLabel(col.key)
        }));
    },
    set(newValue) {
        // When draggable changes order, update columnsData (labels will be stripped)
        columnsData.value = newValue.map(col => ({
            key: col.key,
            visible: col.visible,
            sortable: col.sortable
        }));
    }
});

const visibleColumns = computed(() => columns.value.filter((column) => column.visible))

// Column visibility menu
const columnMenuRef = ref();
const showColumnMenu = ref(false);

const toggleColumnMenu = (event) => {
    showColumnMenu.value = !showColumnMenu.value;
    event.stopPropagation();
};


const sort = ref(props.filters.sort)

const perPage = ref(props.filters.per_page || 10) 

const perPageOptions = computed(() => [
    { label: $t('5 Results'), value: 5 },
    { label: $t('10 Results'), value: 10 },
    { label: $t('15 Results'), value: 15 },
    { label: $t('20 Results'), value: 20 },
    { label: $t('50 Results'), value: 50 },
    { label: $t('100 Results'), value: 100 },
])

// Parse sort for DataTable
const parsedSort = computed(() => {
    const sortValue = sort.value || 'url';
    const isDesc = sortValue.startsWith('-');
    return {
        field: isDesc ? sortValue.substring(1) : sortValue,
        order: isDesc ? -1 : 1
    };
})

// Client-specific filters
const filters = reactive({
    url: props.filters.filter.url,
    sale: { from: props.filters.filter.sale.from, to: props.filters.filter.sale.to },
    da: { from: props.filters.filter.da.from, to: props.filters.filter.da.to },
    dr: { from: props.filters.filter.dr.from, to: props.filters.filter.dr.to },
    traffic: { from: props.filters.filter.traffic.from, to: props.filters.filter.traffic.to },
    // Client-specific filters
    gambling: props.filters.filter.gambling !== undefined ? props.filters.filter.gambling : true,
    sponsor: props.filters.filter.sponsor || false,
    follow: props.filters.filter.follow || false,
    favorites: props.filters.filter.favorites || false,
    interests: props.filters.filter.interests || false,
    recommended: props.filters.filter.recommended || false,
    new: props.filters.filter.new || false,
    link_type: props.filters.filter.link_type
        ? (Array.isArray(props.filters.filter.link_type)
            ? props.filters.filter.link_type
            : [props.filters.filter.link_type])
        : [],
    project_id: props.filters.filter.project_id
        ? (Array.isArray(props.filters.filter.project_id)
            ? props.filters.filter.project_id.map(id => parseInt(id))
            : [parseInt(props.filters.filter.project_id)])
        : [],
    language_id: props.filters.filter.language_id
        ? (Array.isArray(props.filters.filter.language_id)
            ? props.filters.filter.language_id.map(id => parseInt(id))
            : [parseInt(props.filters.filter.language_id)])
        : [],
    country_id: props.filters.filter.country_id
        ? (Array.isArray(props.filters.filter.country_id)
            ? props.filters.filter.country_id.map(id => parseInt(id))
            : [parseInt(props.filters.filter.country_id)])
        : [],
    category_id: props.filters.filter.category_id
        ? (Array.isArray(props.filters.filter.category_id)
            ? props.filters.filter.category_id.map(id => parseInt(id))
            : [parseInt(props.filters.filter.category_id)])
        : [],
    seller_id: props.filters.filter.seller_id
        ? (Array.isArray(props.filters.filter.seller_id)
            ? props.filters.filter.seller_id.map(id => parseInt(id))
            : [parseInt(props.filters.filter.seller_id)])
        : [],
    types: props.filters.filter.types
        ? (Array.isArray(props.filters.filter.types)
            ? props.filters.filter.types.map(id => parseInt(id))
            : [parseInt(props.filters.filter.types)])
        : [],
})

// No need for prop watcher since we're using preserveState
// The filters will maintain their state across requests

const debouncedGet = (() => {
    let timeoutId = null;
    return (delay = 300) => {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        timeoutId = setTimeout(() => {
            get();
            timeoutId = null;
        }, delay);
    };
})();

watch(sort, (newValue, oldValue) => {
    if (newValue !== oldValue) {
        debouncedGet(200);
    }
});

watch(perPage, (newValue, oldValue) => {
    if (newValue !== oldValue && newValue && newValue > 0) {
        debouncedGet(300);
    }
});

// List loading state
const isListLoading = ref(false);

// AbortController for request cancellation
const currentRequestController = ref(null);

// Handler for filter updates from SitesFilters component
const handleFiltersUpdate = (newFilters) => {
    // Mutate the reactive object directly instead of replacing it
    Object.keys(newFilters).forEach(key => {
        if (typeof newFilters[key] === 'object' && newFilters[key] !== null && !Array.isArray(newFilters[key])) {
            // For nested objects like sale, da, dr, traffic
            Object.keys(newFilters[key]).forEach(nestedKey => {
                filters[key][nestedKey] = newFilters[key][nestedKey];
            });
        } else {
            // For primitive values and arrays
            filters[key] = newFilters[key];
        }
    });
};

let stopAutoWatcher = null;

const stopWatcher = () => {
    if (stopAutoWatcher) {
        stopAutoWatcher();
        stopAutoWatcher = null;
    }
};

const handleResetFilters = async () => {
    stopWatcher();

    filters.url = '';
    filters.sale.from = '';
    filters.sale.to = '';
    filters.da.from = '';
    filters.da.to = '';
    filters.dr.from = '';
    filters.dr.to = '';
    filters.traffic.from = '';
    filters.traffic.to = '';
    filters.language_id = [];
    filters.country_id = [];
    filters.category_id = [];
    filters.seller_id = [];
    filters.types = [];
    filters.project_id = [];
    filters.sponsor = false;
    filters.follow = false;
    filters.recommended = false;
    filters.new = false;
    filters.gambling = false;
    filters.favorites = false;
    filters.interests = false;
    filters.link_type = [];

    await nextTick();

    // Immediately trigger search after reset
    get();

    startAutoWatcher();
}

const startAutoWatcher = () => {

    stopWatcher();

    stopAutoWatcher = watchDebounced(() => ({ ...filters }), (n, o) => {
        console.log('[Sites Debug] watchDebounced triggered → calling get()', { new: n, old: o });
        get();
    }, {
        debounce: 1000,
        deep: true,
    });
};

startAutoWatcher();

// Commented out coin store watcher since we're using mock data
// watch(() => coinStore.coin, (n, o) => {
//     filters.sale.from = Math.ceil(`${filters.sale.from}`.replace(/\D/g, '') / coinStore.ratios[o])
//     filters.sale.to = Math.ceil(`${filters.sale.to}`.replace(/\D/g, '') / coinStore.ratios[o])
// })

// Helper function to remove empty/null/undefined values from filter object
const cleanFilters = (filters) => {
    const cleaned = {};

    for (const [key, value] of Object.entries(filters)) {
        // Skip if value is null, undefined, empty string, or empty array
        if (value === null || value === undefined || value === '') {
            continue;
        }

        // Handle arrays
        if (Array.isArray(value)) {
            if (value.length > 0) {
                cleaned[key] = value;
            }
            continue;
        }

        // Handle objects (like sale, da, dr, traffic)
        if (typeof value === 'object') {
            const cleanedObject = {};
            let hasValues = false;

            for (const [subKey, subValue] of Object.entries(value)) {
                if (subValue !== null && subValue !== undefined && subValue !== '') {
                    cleanedObject[subKey] = subValue;
                    hasValues = true;
                }
            }

            if (hasValues) {
                cleaned[key] = cleanedObject;
            }
            continue;
        }

        // Handle booleans - only include if true
        if (typeof value === 'boolean') {
            if (value === true) {
                cleaned[key] = value;
            }
            continue;
        }

        // Include all other values
        cleaned[key] = value;
    }

    return cleaned;
};

const get = async () => {
    console.log('[Sites Debug] get() called', new Error().stack?.split('\n').slice(1, 4).join('\n'));
    console.log('[Sites Debug] get() timestamp:', new Date().toISOString());
    try {
        // Cancel previous request if exists
        if (currentRequestController.value) {
            currentRequestController.value.abort();
        }

        // Create new abort controller
        currentRequestController.value = new AbortController();


        // Detectar se estamos em context admin ou client baseado na URL atual
        const currentPath = window.location.pathname;
        let routeName;

        if (currentPath.includes('/admin/')) {
            // Context admin - usar rota admin
            routeName = proxy.route ? proxy.route('sites.index') : '/admin/sites';
        } else {
            // Context client - usar rota client (client.sites.index é mapeada para 'sites')
            routeName = proxy.route ? proxy.route('client.sites.index') : '/sites';
        }


        const filterData = {
            url: filters.url,
            sale: filters.sale,
            da: filters.da,
            dr: filters.dr,
            traffic: filters.traffic,
            // Client-specific filters
            follow: filters.follow,
            // recommended: filters.recommended,
            // new: filters.new,
            project_id: Array.isArray(filters.project_id) && filters.project_id.length > 0 ? filters.project_id : [],
            language_id: Array.isArray(filters.language_id) && filters.language_id.length > 0 ? filters.language_id : [],
            country_id: Array.isArray(filters.country_id) && filters.country_id.length > 0 ? filters.country_id : [],
            category_id: Array.isArray(filters.category_id) && filters.category_id.length > 0 ? filters.category_id : [],
            seller_id: Array.isArray(filters.seller_id) && filters.seller_id.length > 0 ? filters.seller_id : [],
            types: Array.isArray(filters.types) && filters.types.length > 0 ? filters.types.join(',') : null,
            link_type: filters.link_type,
        };

        // Only send these filters when they are true
        if (filters.sponsor === true) {
            filterData.sponsor = 1;
        }

        if (filters.favorites === true) {
            filterData.favorites = true;
        }

        if (filters.interests === true) {
            filterData.interests = true;
        }

        if (filters.recommended === true) {
            filterData.recommended = true;
        }

        if (filters.new === true) {
            filterData.new = true;
        }

        if (filters.gambling === true) {
            filterData.gambling = true;
        }

        const requestParams = {
            sort: sort.value,
            ratios: coinStore.ratios,
            filter: cleanFilters(filterData),
            page: 1, // Reset to page 1 when filters change
            per_page: perPage.value,
        };

        console.log('[Sites Debug] router.get() → sending request to:', routeName);
        console.log('[Sites Debug] request params:', JSON.parse(JSON.stringify(requestParams)));
        router.get(routeName, requestParams, {
            preserveState: true,
            preserveScroll: true,
            only: ['sites', 'filters'],
            onStart: () => {
                console.log('[Sites Debug] request STARTED at:', new Date().toISOString());
                isListLoading.value = true;
            },
            onFinish: () => {
                console.log('[Sites Debug] request FINISHED at:', new Date().toISOString());
                isListLoading.value = false;
                currentRequestController.value = null;
            },
            onError: (errors) => {
                console.error('[Sites Debug] request ERROR at:', new Date().toISOString(), errors);
                isListLoading.value = false;
                currentRequestController.value = null;
            },
            signal: currentRequestController.value?.signal
        })
    } catch (error) {
        console.error('Error in get function:', error);
        isListLoading.value = false;
        currentRequestController.value = null;
    }
}

// Filters modal
const openFiltersModal = ref(false)

watch(openFiltersModal, async (newVal, oldVal) => {
    if (oldVal === true && newVal === false) {
        const activeElement = document.activeElement;
        if (activeElement && activeElement.tagName === 'INPUT') {
            activeElement.blur();
        }

        await nextTick();

        setTimeout(() => {
            startAutoWatcher();
        }, 100);
    }
});

// Pagination handler
const handlePageChange = (event) => {
    try {
        // event.page is 0-indexed, Laravel expects 1-indexed
        const targetPage = event.page + 1;
        const routeName = proxy.route ? proxy.route(getRouteName()) : '/sites';

        isListLoading.value = true;

        router.get(routeName, {
            sort: sort.value,
            ratios: coinStore.ratios,
            filter: buildFilterData(),
            page: targetPage,
            per_page: perPage.value,
        }, {
            only: ['sites', 'filters'],
            preserveScroll: true,
            preserveState: true,
            onFinish: () => {
                isListLoading.value = false;
            },
            onError: () => {
                isListLoading.value = false;
            }
        });
    } catch (e) {
        console.error(e);
        isListLoading.value = false;
    }
}

// Mobile pagination handlers
const handlePreviousPage = () => {
    if (props.sites.current_page > 1) {
        const targetPage = props.sites.current_page - 1;
        const routeName = proxy.route ? proxy.route(getRouteName()) : '/sites';

        isListLoading.value = true;

        router.get(routeName, {
            sort: sort.value,
            ratios: coinStore.ratios,
            filter: buildFilterData(),
            page: targetPage,
            per_page: perPage.value,
        }, {
            only: ['sites', 'filters'],
            preserveScroll: true,
            preserveState: true,
            onFinish: () => {
                isListLoading.value = false;
            },
            onError: () => {
                isListLoading.value = false;
            }
        });
    }
}

const handleNextPage = () => {
    if (props.sites.current_page < props.sites.last_page) {
        const targetPage = props.sites.current_page + 1;
        const routeName = proxy.route ? proxy.route(getRouteName()) : '/sites';

        isListLoading.value = true;

        router.get(routeName, {
            sort: sort.value,
            ratios: coinStore.ratios,
            filter: buildFilterData(),
            page: targetPage,
            per_page: perPage.value,
        }, {
            only: ['sites', 'filters'],
            preserveScroll: true,
            preserveState: true,
            onFinish: () => {
                isListLoading.value = false;
            },
            onError: () => {
                isListLoading.value = false;
            }
        });
    }
}

// AI Search functionality
const openAiSearch = ref(false)
const aiSearchLoading = ref(false)
const aiSearchQuery = ref('')


// Active filters count
const activeFiltersCount = computed(() => {
    let count = 0

    // Check URL filter
    if (filters.url) count++

    // Check range filters (only if different from defaults)
    if (filters.sale.from || filters.sale.to) count++
    if (filters.da.from || filters.da.to) count++
    if (filters.dr.from || filters.dr.to) count++
    if (filters.traffic.from || filters.traffic.to) count++

    // Check boolean filters
    if (filters.sponsor) count++
    if (filters.follow) count++
    if (filters.favorites) count++
    if (filters.interests) count++
    if (filters.recommended) count++
    if (filters.new) count++
    if (filters.gambling) count++
    if (filters.link_type && filters.link_type.length > 0) count++

    // Check array filters
    if (filters.language_id && filters.language_id.length > 0) count++
    if (filters.country_id && filters.country_id.length > 0) count++
    if (filters.category_id && filters.category_id.length > 0) count++
    if (filters.seller_id && filters.seller_id.length > 0) count++
    if (filters.types && filters.types.length > 0) count++
    if (filters.project_id && filters.project_id.length > 0) count++

    return count
})

const handleAiSearch = () => {
    aiSearchLoading.value = true

    try {
        // Detectar context baseado na URL atual
        const currentPath = window.location.pathname;
        let routeName;

        if (currentPath.includes('/admin/')) {
            routeName = proxy.route ? proxy.route('sites.aiSearch') : '/admin/sites/ai-search';
        } else {
            routeName = proxy.route ? proxy.route('client.sites.aiSearch') : '/sites/ai-search';
        }

        // Full-page loading is handled automatically by app.js for page navigations
        router.get(routeName, {
            text: aiSearchQuery.value,
        }, {
            preserveScroll: true,
            onFinish() {
                aiSearchLoading.value = false
                openAiSearch.value = false
            },
            onError: () => {
                aiSearchLoading.value = false
            }
        })
    } catch (error) {
        console.error('Error in AI search:', error);
        aiSearchLoading.value = false
        openAiSearch.value = false
        hidePageLoading();
    }
}

// Import functionality
const importFilepond = ref(null)
const openImportDialog = ref(false)
const importFinished = ref(false)
const seller = ref(null)
const notifyAdded = ref(false)
const notifyUpdated = ref(false)

const uploadSites = (fieldName, file, metadata, load, error, progress, abort) => {
    try {
        // Detectar context baseado na URL atual
        const currentPath = window.location.pathname;
        let routeName;

        if (currentPath.includes('/admin/')) {
            routeName = proxy.route ? proxy.route('sites.import') : '/admin/sites/import';
        } else {
            routeName = proxy.route ? proxy.route('client.sites.import') : '/sites/import';
        }

        router.post(routeName, {
            file: file,
            seller_id: seller.value,
            notify_added: notifyAdded.value,
            notify_updated: notifyUpdated.value,
        }, {
            forceFormData: true,
            preserveScroll: true,
            preserveState: true,
            onStart: () => {
                showPageLoading($t('Importing sites...'));
            },
            onError(err) {
                abort()
                hidePageLoading();
            },
            onSuccess(res) {
                load()
            },
            onProgress(p) {
                progress(true, p.loaded, p.total);
            },
            onFinish() {
                importFinished.value = true
                hidePageLoading();
            }
        })
    } catch (err) {
        console.error('Error uploading sites:', err);
        abort();
        hidePageLoading();
    }
}

watch(openImportDialog, (n, o) => {
    if (!n) {
        seller.value = null
        notifyAdded.value = false
        notifyUpdated.value = false

        setTimeout(() => {
            importFinished.value = false
            router.reload({
                only: ['importFailures', 'importErrors', 'importAdded', 'importUpdated'],
            })
        }, 300)
    }
})

const coinFormat = computed(() => props.coins[coinStore.coin])
const expanded = ref([])

// Helper functions for site actions
const toggleFavorite = async (siteId) => {
    try {
        const routeName = proxy.route ? proxy.route('client.sites.favorite', siteId) : `/sites/${siteId}/favorite`;
        await router.post(routeName, {}, {
            preserveState: true,
            preserveScroll: true
        });
        // Optionally reload data or update UI state
    } catch (e) {
        console.error('Error toggling favorite:', e);
    }
}

const toggleInterest = async (siteId) => {
    try {
        const routeName = proxy.route ? proxy.route('client.sites.interest', siteId) : `/sites/${siteId}/interest`;
        await router.post(routeName, {}, {
            preserveState: true,
            preserveScroll: true
        });
        // Optionally reload data or update UI state
    } catch (e) {
        console.error('Error toggling interest:', e);
    }
}

// Project dialog state
const showProjectDialog = ref(false);
const selectedSiteForProject = ref(null);
const selectedProjectId = ref(null);
const confirmRemoveDialog = ref(false);
const siteToRemove = ref(null);
const projectToRemoveFrom = ref(null);
const isViewingProjects = ref(false);

const addToProject = (siteId) => {
    selectedSiteForProject.value = siteId;
    selectedProjectId.value = null;
    isViewingProjects.value = false;
    showProjectDialog.value = true;
}

const showProject = (siteId) => {
    selectedSiteForProject.value = siteId;
    selectedProjectId.value = null;
    isViewingProjects.value = true;
    showProjectDialog.value = true;
}

const isSiteInProject = (projectId, siteId) => {
    const site = sitesWithHighestSale.value.find(s => s.id === siteId);
    if (!site || !site.project_ids) return false;

    const projectIds = site.project_ids.toString().split(',').map(id => parseInt(id));
    return projectIds.includes(projectId);
}

const confirmAddToProject = async () => {
    if (!selectedProjectId.value) {
        toast.add({
            severity: 'warn',
            summary: $t('Warning'),
            detail: $t('Please select a project'),
            life: 3000
        });
        return;
    }

    try {
        const routeName = proxy.route ?
            proxy.route('client.sites.project', { site: selectedSiteForProject.value, project: selectedProjectId.value }) :
            `/sites/${selectedSiteForProject.value}/project/${selectedProjectId.value}`;

        await router.post(routeName, {}, {
            onSuccess: () => {
                showProjectDialog.value = false;
                toast.add({
                    severity: 'success',
                    summary: $t('Success'),
                    detail: $t('Site added to project successfully'),
                    life: 3000
                });
            },
            onError: () => {
                toast.add({
                    severity: 'error',
                    summary: $t('Error'),
                    detail: $t('Failed to add site to project'),
                    life: 3000
                });
            }
        });
    } catch (e) {
        console.error('Error adding to project:', e);
    }
}

const confirmRemoveFromProject = async (projectId) => {
    siteToRemove.value = selectedSiteForProject.value;
    projectToRemoveFrom.value = props.projects.find(p => p.id === projectId);
    confirmRemoveDialog.value = true;
}

const executeRemoveFromProject = async () => {
    try {
        await removeFromProject(siteToRemove.value, projectToRemoveFrom.value.id);
        confirmRemoveDialog.value = false;
        siteToRemove.value = null;
        projectToRemoveFrom.value = null;

        if (isViewingProjects.value) {
            showProjectDialog.value = false;
        }
    } catch (e) {
        console.error('Error removing from project:', e);
    }
}

// Helper function to check if site is in favorites/interests
const isFavorite = (siteId) => {
    return props.favorites && props.favorites.includes(siteId);
}

const isInterest = (siteId) => {
    return props.interests && props.interests.includes(siteId);
}

// Helper function to check if site is in projects (for flag icon)
const isSiteInProjects = (siteId) => {
    const site = sitesWithHighestSale.value.find(s => s.id === siteId);
    return site && site.project_ids && site.project_ids.toString().length > 0;
}

// Helper function to get project names for a site
const getSiteProjects = (siteId) => {
    const site = sitesWithHighestSale.value.find(s => s.id === siteId);
    if (!site || !site.project_ids) return [];

    const projectIds = site.project_ids.toString().split(',').map(id => parseInt(id));
    return props.projects.filter(p => projectIds.includes(p.id));
}

// Helper function to get the correct route name based on context
const getRouteName = () => {
    if (typeof window === 'undefined') return 'client.sites.index';
    const currentPath = window.location.pathname;
    return currentPath.includes('/admin/') ? 'sites.index' : 'client.sites.index';
}

// Helper function to build filter data
const buildFilterData = () => {
    const filterData = {
        url: filters.url,
        sale: filters.sale,
        da: filters.da,
        dr: filters.dr,
        traffic: filters.traffic,
        follow: filters.follow,
        project_id: Array.isArray(filters.project_id) && filters.project_id.length > 0 ? filters.project_id : [],
        language_id: Array.isArray(filters.language_id) && filters.language_id.length > 0 ? filters.language_id : [],
        country_id: Array.isArray(filters.country_id) && filters.country_id.length > 0 ? filters.country_id : [],
        category_id: Array.isArray(filters.category_id) && filters.category_id.length > 0 ? filters.category_id : [],
        seller_id: Array.isArray(filters.seller_id) && filters.seller_id.length > 0 ? filters.seller_id : [],
        types: Array.isArray(filters.types) && filters.types.length > 0 ? filters.types.join(',') : null,
    };

    // Only send these filters when they are true
    if (filters.sponsor === true) {
        filterData.sponsor = 1;
    }
    if (filters.favorites === true) {
        filterData.favorites = true;
    }
    if (filters.interests === true) {
        filterData.interests = true;
    }
    if (filters.recommended === true) {
        filterData.recommended = true;
    }
    if (filters.new === true) {
        filterData.new = true;
    }
    if (filters.gambling === true) {
        filterData.gambling = true;
    }

    return cleanFilters(filterData);
}

// Function to remove site from project
const removeFromProject = async (siteId, projectId) => {
    try {
        const routeName = proxy.route ?
            proxy.route('client.sites.project', { site: siteId, project: projectId }) :
            `/sites/${siteId}/project/${projectId}`;

        await router.post(routeName, {}, {
            onSuccess: () => {
                toast.add({
                    severity: 'success',
                    summary: $t('Success'),
                    detail: $t('Site removed from project successfully'),
                    life: 3000
                });
            },
            onError: () => {
                toast.add({
                    severity: 'error',
                    summary: $t('Error'),
                    detail: $t('Failed to remove site from project'),
                    life: 3000
                });
            }
        });
    } catch (e) {
        console.error('Error removing from project:', e);
    }
}

// Helper function to open external site safely
const openExternalSite = (url) => {
    try {
        if (!url) {
            console.error('No URL provided');
            return;
        }

        // Remove any protocols and clean the URL
        const cleanUrl = url.replace(/^https?:\/\//, '').trim();

        if (!cleanUrl) {
            console.error('Invalid URL after cleaning:', url);
            return;
        }

        // Construct full URL with https
        const fullUrl = `https://${cleanUrl}`;
        console.log('Opening external site:', fullUrl);

        window.open(fullUrl, '_blank', 'noopener,noreferrer');
    } catch (e) {
        console.error('Error opening external site:', e, 'URL:', url);
    }
}

// Column menu
const columnMenuVisible = ref(false)

// Sort handler
const onSort = (event) => {
    // Spatie query builder uses minus prefix for descending sort
    sort.value = (event.sortOrder === 1 ? '' : '-') + event.sortField
}

// Client-specific handlers for SitesTable events
const handleClientPageChange = ({ event, page }) => {
    if (event) {
        // Desktop pagination
        handlePageChange(event);
    } else {
        // Mobile pagination
        if (page < props.sites.current_page) {
            handlePreviousPage();
        } else {
            handleNextPage();
        }
    }
}

const handleSiteClick = (site) => {
    try {
        const routeName = proxy.route ? proxy.route('client.sites.show', site.id) : `/sites/${site.id}`;
        router.visit(routeName);
    } catch (e) {
        console.error(e);
    }
}

// Helper function to check if any filters are active
const hasActiveFilters = () => {
    // Check URL filter
    if (filters.url) return true;

    // Check range filters
    if (filters.sale.from || filters.sale.to) return true;
    if (filters.da.from || filters.da.to) return true;
    if (filters.dr.from || filters.dr.to) return true;
    if (filters.traffic.from || filters.traffic.to) return true;

    // Check boolean filters
    if (filters.sponsor) return true;
    if (filters.follow) return true;
    if (filters.favorites) return true;
    if (filters.interests) return true;
    if (filters.recommended) return true;
    if (filters.new) return true;
    if (filters.link_type && filters.link_type.length > 0) return true;

    // Check array filters
    if (filters.language_id && filters.language_id.length > 0) return true;
    if (filters.country_id && filters.country_id.length > 0) return true;
    if (filters.category_id && filters.category_id.length > 0) return true;
    if (filters.seller_id && filters.seller_id.length > 0) return true;
    if (filters.types && filters.types.length > 0) return true;
    if (filters.project_id && filters.project_id.length > 0) return true;

    return false;
}
const defaultColumnsData = columnsData.value.map(col => ({ ...col }));

const resetToDefault = () => {
  columnsData.value = defaultColumnsData.map(col => ({ ...col }));
};

onMounted(async () => {
    console.log('[Sites Debug] onMounted called');
    console.log('[Sites Debug] filters state:', JSON.parse(JSON.stringify(filters)));
    console.log('[Sites Debug] hasActiveFilters():', hasActiveFilters());

    try {
        await loadCart();
    } catch (error) {
        console.error('Error loading cart on mount:', error);
    }

    // Apply filters on initial load if any filters are active
    if (hasActiveFilters()) {
        console.log('[Sites Debug] hasActiveFilters=true → calling get() (DUPLICATE REQUEST)');
        get();
    } else {
        console.log('[Sites Debug] hasActiveFilters=false → NO duplicate request');
    }

    // Initialize tooltips
    setTimeout(() => {
        if (window.tippy) {
            window.tippy('[data-tippy-content]');
        }
    }, 500)

    // Close column menu when clicking outside
    document.addEventListener('click', (e) => {
        if (columnMenuRef.value && !columnMenuRef.value.contains(e.target) &&
            !e.target.closest('.column-menu-button')) {
            showColumnMenu.value = false;
        }
    });
})
</script>

<template>
    <Toast position="top-right" pt:root:class="!top-20"/>
    <!-- AI Search Dialog -->
    <Dialog v-model:visible="openAiSearch" modal :header="$t('Search with AI')" :style="{ width: '95vw', maxWidth: '25rem' }" :breakpoints="{ '960px': '75vw', '641px': '95vw' }">
        <div class="flex flex-col gap-4">
            <Textarea
                v-model="aiSearchQuery"
                rows="4"
                class="w-full"
                :placeholder="$t('Describe what kind of websites you are looking for...')"
            />
        </div>
        <template #footer>
            <Button
                :label="$t('Cancel')"
                severity="secondary"
                @click="openAiSearch = false"
                autofocus
            />
            <Button
                :label="aiSearchLoading ? $t('Searching...') : $t('Search')"
                :disabled="aiSearchLoading"
                @click="handleAiSearch"
            />
        </template>
    </Dialog>

    <!-- Import Dialog -->
    <Dialog v-model:visible="openImportDialog" modal header="Import Sites" :style="{ width: '95vw', maxWidth: '40rem' }" :breakpoints="{ '960px': '75vw', '641px': '95vw' }">
        <div class="flex flex-col gap-4">
            <FileUpload
                ref="importFilepond"
                mode="basic"
                name="file"
                accept=".csv"
                :maxFileSize="1000000"
                :auto="true"
                choose-label="Choose CSV File"
                @upload="uploadSites"
            />

            <div v-if="!importFinished">
                <Select
                    v-model="seller"
                    :options="sellers"
                    optionLabel="name"
                    optionValue="id"
                    placeholder="Select a seller"
                    class="w-full"
                />

                <div class="flex flex-col gap-2 mt-4">
                    <div class="flex items-center">
                        <Checkbox v-model="notifyAdded" inputId="notify-added" :binary="true" />
                        <label for="notify-added" class="ml-2">Notify added sites</label>
                    </div>
                    <div class="flex items-center">
                        <Checkbox v-model="notifyUpdated" inputId="notify-updated" :binary="true" />
                        <label for="notify-updated" class="ml-2">Notify updated sites</label>
                    </div>
                </div>
            </div>

            <div v-if="importFinished" class="text-center">
                <div v-if="importAdded">{{ importAdded.length }} sites added.</div>
                <div v-if="importUpdated">{{ importUpdated.length }} sites updated.</div>
                <div v-if="importFailures">{{ importFailures.length }} sites failed.</div>
                <div v-if="importErrors">{{ importErrors.length }} sites with errors.</div>
            </div>
        </div>
    </Dialog>

    <!-- Project Selection Dialog -->
    <Dialog
        v-model:visible="showProjectDialog"
        :header="isViewingProjects ? $t('Site Projects') : $t('Add to Project')"
        :modal="true"
        :style="{ width: '450px' }"
    >
        <div class="flex flex-col gap-4">
            <p class="text-surface-600">
                {{ isViewingProjects ? $t('This site is in the following projects:') : $t('Select a project to add this site to:') }}
            </p>

            <div v-if="projects && projects.length > 0" class="flex flex-col gap-3">
                <div v-for="project in (isViewingProjects ? projects.filter(p => isSiteInProject(p.id, selectedSiteForProject)) : projects)" :key="project.id"
                    class="flex items-center gap-3 p-3 border border-surface-200 rounded-lg relative"
                    :class="{
                        'bg-primary-50 border-primary-300': selectedProjectId === project.id,
                        'cursor-not-allowed': isSiteInProject(project.id, selectedSiteForProject) && !isViewingProjects,
                        'hover:bg-surface-50 cursor-pointer': !isSiteInProject(project.id, selectedSiteForProject) && !isViewingProjects
                    }"
                    @click="!isViewingProjects && !isSiteInProject(project.id, selectedSiteForProject) && (selectedProjectId = project.id)"
                >
                    <RadioButton
                        v-if="!isViewingProjects"
                        :modelValue="selectedProjectId"
                        :value="project.id"
                        :inputId="`project-${project.id}`"
                        :disabled="isSiteInProject(project.id, selectedSiteForProject)"
                        :class="{ 'opacity-60': isSiteInProject(project.id, selectedSiteForProject) }"
                    />
                    <div
                        class="w-10 h-10 rounded flex items-center justify-center text-white font-semibold text-sm"
                        :style="{ backgroundColor: project.color }"
                        :class="{ 'opacity-60': isSiteInProject(project.id, selectedSiteForProject) }"
                    >
                        {{ project.name.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2) }}
                    </div>
                    <label :for="!isViewingProjects ? `project-${project.id}` : undefined" class="flex-1" :class="{
                        'cursor-pointer': !isSiteInProject(project.id, selectedSiteForProject) && !isViewingProjects,
                        'cursor-not-allowed opacity-60': isSiteInProject(project.id, selectedSiteForProject) && !isViewingProjects
                    }">
                        <div class="font-semibold text-surface-900 flex items-center gap-2">
                            {{ project.name }}
                            <span v-if="isSiteInProject(project.id, selectedSiteForProject)"
                                class="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full text-green-800 bg-green-100">
                                <i class="pi pi-check text-xs mr-1"></i>
                                {{ $t('Already added') }}
                            </span>
                        </div>
                        <div class="text-sm text-surface-600">
                            {{ project.sites_count ?? 0 }} {{ (project.sites_count ?? 0) === 1 ? $t('website') : $t('websites') }}
                        </div>
                    </label>

                    <div v-if="isSiteInProject(project.id, selectedSiteForProject)" class="ml-auto">
                        <Button
                            icon="pi pi-times"
                            severity="danger"
                            size="small"
                            text
                            rounded
                            @click="confirmRemoveFromProject(project.id)"
                            v-tooltip.top="$t('Remove from project')"
                        />
                    </div>
                </div>
            </div>

            <div v-else-if="!isViewingProjects" class="text-center py-8">
                <i class="pi pi-folder-open text-4xl text-surface-300 mb-3"></i>
                <p class="text-surface-600 mb-3">{{ $t('No projects found') }}</p>
                <Button
                    :label="$t('Create Project')"
                    icon="pi pi-plus"
                    @click="() => { showProjectDialog = false; router.get(proxy.route ? proxy.route('client.projects.create') : '/projects/create'); }"
                    severity="primary"
                    size="small"
                />
            </div>

            <div v-else class="text-center py-8">
                <i class="pi pi-folder text-4xl text-surface-300 mb-3"></i>
                <p class="text-surface-600 mb-3">{{ $t('This site is not in any project yet') }}</p>
            </div>
        </div>

        <template #footer>
            <Button
                :label="$t('Cancel')"
                text
                @click="showProjectDialog = false"
            />
            <Button
                v-if="!isViewingProjects"
                :label="$t('Add to Project')"
                @click="confirmAddToProject"
                :disabled="!selectedProjectId"
                severity="primary"
            />
        </template>
    </Dialog>

    <!-- Remove from Project Confirmation Dialog -->
    <Dialog
        v-model:visible="confirmRemoveDialog"
        :style="{ width: '450px' }"
        :header="$t('Confirm Removal')"
        :modal="true"
    >
        <div class="flex items-center gap-4">
            <i class="pi pi-exclamation-triangle text-3xl text-orange-500" />
            <span v-if="projectToRemoveFrom">
                {{ $t('Are you sure you want to remove this site from') }} <b>{{ projectToRemoveFrom.name }}</b>?
            </span>
        </div>
        <template #footer>
            <Button
                :label="$t('Cancel')"
                icon="pi pi-times"
                text
                @click="confirmRemoveDialog = false"
            />
            <Button
                :label="$t('Remove')"
                icon="pi pi-trash"
                severity="danger"
                @click="executeRemoveFromProject"
            />
        </template>
    </Dialog>

    <!-- Main Content -->
    <div class="w-full max-w-full">
        <!-- Sync Banner -->
        <SyncBanner />

        <!-- Filters Component -->
        <div class="px-2 sm:px-4 lg:px-0 mb-4">
            <SitesFilters
                :modelValue="filters"
                @update:modelValue="handleFiltersUpdate"
                @reset-filters="handleResetFilters"
                v-model:filtersModalVisible="openFiltersModal"
                :countries="countries"
                :languages="languages"
                :categories="categories"
                :sellers="sellers"
                :types="types"
                :projects="projects"
                :sitesByCountry="sitesByCountry"
                :coinStore="coinStore"
                :showProjectFilter="true"
                :showFavoritesFilter="true"
                :showInterestsFilter="true"
            />
        </div>

        <!-- Results Section -->
        <div class="px-2 sm:px-4 lg:px-0 mb-4">
            <div class="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-3 lg:gap-4">
                <div class="flex-1 flex items-center gap-3">
                    <span class="ms-2 text-lg text-surface-800">
                        {{ sites.total.toLocaleString() }} {{ $t('results found') }}
                    </span>
                    <div v-if="pendingCount > 0">
                        <Button
                            :label="`${pendingCount} sites waiting for approval.`"
                            link
                            class="p-0 text-sm text-primary-500"
                            @click="() => {
                                try {
                                    const routeName = proxy.route ? proxy.route('client.sites.index', { 'filter[of_status]': 'PENDING' }) : '/sites';
                                    isListLoading.value = true;
                                    router.visit(routeName, {
                                        only: ['sites', 'filters'],
                                        preserveScroll: false,
                                        onFinish: () => {
                                            isListLoading.value = false;
                                        },
                                        onError: () => {
                                            isListLoading.value = false;
                                        }
                                    });
                                } catch (e) {
                                    console.error(e);
                                    isListLoading.value = false;
                                }
                            }"
                        />
                    </div>
                </div>

                <!-- Desktop: Columns button -->
                <div class="hidden lg:flex gap-2 relative">
                    <Select
                        v-model="perPage"
                        :options="perPageOptions"
                        optionLabel="label"
                        optionValue="value"
                        class="h-[31px] custom-select-center"
                        style="width: 150px;"
                        :showClear="false"
                    />
                    <Button
                        :label="$t('Columns')"
                        icon="pi pi-cog"
                        severity="secondary"
                        outlined
                        size="small"
                        @click="toggleColumnMenu"
                        class="column-menu-button h-[31px]"
                    />
                    <div ref="columnMenuRef" v-show="showColumnMenu"
                         class="absolute right-0 top-10 z-50 bg-white rounded-lg shadow-lg border border-surface-200 column-menu">
                        <div class="p-3 border-b border-surface-200">
                            <div class="flex items-center justify-between mb-0">
                                <div class="font-semibold text-surface-700">{{ $t('Manage Columns') }}</div>
                                <Button
                                    icon="pi pi-replay"
                                    size="small"
                                    outlined
                                    class="uncheck-all-button"
                                    @click="resetToDefault"
                                    :pt="{
                                        root: {
                                            style: 'border-color: #ced4da !important;'
                                        },
                                        icon: {
                                            style: 'color: #adb5bd !important;'
                                        }
                                    }"
                                />
                            </div>
                            <div class="text-sm text-surface-500">{{ $t('Drag to reorder, check to show/hide') }}</div>
                        </div>
                        <div class="p-3 max-h-96 overflow-y-auto">
                            <draggable
                                v-model="columnsData"
                                tag="div"
                                ghost-class="opacity-50"
                                handle=".drag-handle"
                                :animation="200"
                            >
                                <template #item="{element}">
                                    <div class="flex items-center gap-2 p-2 hover:bg-surface-100 rounded cursor-move">
                                        <i class="pi pi-bars drag-handle text-surface-400 cursor-move"></i>
                                        <Checkbox
                                            v-model="element.visible"
                                            :binary="true"
                                            :inputId="`col-${element.key}`"
                                        />
                                        <label :for="`col-${element.key}`" class="flex-1 cursor-pointer text-sm select-none">
                                            {{ getColumnLabel(element.key) }}
                                        </label>
                                    </div>
                                </template>
                            </draggable>
                        </div>
                    </div>
                </div>
            </div>
        </div>


        <!-- Sites Table Component -->
        <div class="px-2 sm:px-4 lg:px-0">
            <SitesTable
                :sites="sites"
                :sitesWithHighestSale="sitesWithHighestSale"
                :visibleColumns="visibleColumns"
                :isListLoading="isListLoading"
                :parsedSort="parsedSort"
                :coinStore="coinStore"
                :allTypes="types"
                mode="client"
                :favorites="favorites"
                :projects="projects"
                :rocketFillSvg="rocketFillSvg"
                :showPageLoading="showPageLoading"
                :hidePageLoading="hidePageLoading"
                @sort="onSort"
                @page-change="handleClientPageChange"
                @site-click="handleSiteClick"
                @toggle-favorite="toggleFavorite"
                @add-to-project="addToProject"
                @show-project="showProject"
                @remove-from-project="removeFromProject"
            />
        </div>
    </div>
</template>

<style scoped>
.column-menu {
    min-width: 300px;
    max-width: 400px;
}

.custom-select-center :deep(.p-select-label),
.custom-select-center :deep(.p-select-option),
.custom-select-center :deep(.p-select-dropdown .p-select-option) {
    white-space: nowrap !important;
    color: #64748b !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    font: 14px Archivo, Helvetica, Arial, sans-serif !important;
    padding: 6px 10px 6px 8px !important;
}

.custom-select-center :deep(.p-select-label) {
    display: flex !important;
    align-items: center !important;
    max-width: 120px !important;
    font: 14px Archivo, Helvetica, Arial, sans-serif !important;
}

.column-menu-button {
    border: 1px solid #d1d5db !important;
    border-radius: 6px !important;
}

.column-menu-button:hover {
    border-color: #9ca3af !important;
}

</style>
